import { useClearRolodexEntriesCachedByIdOnScopeChange } from 'features/knowledgeDocumentViewer/hooks/useRolodexEntriesCachedById'
import {
  clearPreviewModePreference,
  readPreviewModePreference,
  writePreviewModePreference,
} from 'features/knowledgeDocumentViewer/utils/previewModeStorage'
import { activePipelineConfigLoadOptions } from 'features/pipelineConfig/activePipelineConfigLoadOptions'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

type PipelineConfigTags = Record<string, string | string[]>

type PipelineConfigStatusEntry = {
  entry: {
    id: string
  }
}

interface PipelineConfigContextType {
  isPreviewMode: boolean
  setIsPreviewMode: React.Dispatch<React.SetStateAction<boolean>>
  activePipelineConfigId: string | undefined
  previewPipelineConfigId: string | undefined
  selectedPipelineConfigId: string | undefined
  pipelineConfigCacheScope: string | undefined
  hasPreviewConfig: boolean
  pipelineConfigScopeError: Error | undefined
  retryPipelineConfigScope: () => Promise<void>
  isPipelineConfigScopeLoading: boolean
  getPipelineConfigScopedTags: (
    tags: PipelineConfigTags
  ) => PipelineConfigTags | undefined
}

const PipelineConfigContext = createContext<
  PipelineConfigContextType | undefined
>(undefined)

/** The scope gate blocks every document read; a transient config-load failure
 * must not park it in loading for the rest of the session. */
const PIPELINE_CONFIG_RETRY = { shouldRetryOnError: true, errorRetryCount: 2 }

export const PipelineConfigProvider: React.FC<{
  children: React.ReactNode
  siteID: string
  /**
   * Pin to active pipeline config — preview scope cannot resolve fusion/subject document ids.
   */
  forceActiveConfig?: boolean
}> = ({ children, siteID, forceActiveConfig = false }) => {
  const [isPreviewMode, setIsPreviewModeState] = useState(false)
  const [hasResolvedPreviewPreference, setHasResolvedPreviewPreference] =
    useState(false)
  const {
    data: activePipelineConfigEntries,
    error: activePipelineConfigError,
    mutate: mutateActivePipelineConfig,
  } = useGetRolodexEntriesByLoadOptions<PipelineConfigStatusEntry[]>(
    activePipelineConfigLoadOptions(siteID),
    PIPELINE_CONFIG_RETRY
  )
  const {
    data: previewPipelineConfigEntries,
    error: previewPipelineConfigError,
    mutate: mutatePreviewPipelineConfig,
  } = useGetRolodexEntriesByLoadOptions<PipelineConfigStatusEntry[]>(
    forceActiveConfig
      ? undefined
      : {
          type: ['pipeline-config'],
          tags: { site: siteID },
          filters: [
            { field: 'data', key: 'status', op: '=', value: 'preview' },
          ],
          limit: 1,
        },
    PIPELINE_CONFIG_RETRY
  )

  const activePipelineConfigId = activePipelineConfigEntries?.[0]?.entry.id
  const previewPipelineConfigId = previewPipelineConfigEntries?.[0]?.entry.id
  // An errored load still resolves the gate — with no config found — so one
  // failure can't stick every downstream document read in loading.
  const hasLoadedPreviewPipelineConfig =
    previewPipelineConfigEntries !== undefined ||
    previewPipelineConfigError !== undefined
  const hasPreviewConfig = previewPipelineConfigId !== undefined
  const selectedPipelineConfigId = isPreviewMode
    ? previewPipelineConfigId
    : activePipelineConfigId
  const hasLoadedSelectedPipelineConfig = isPreviewMode
    ? hasLoadedPreviewPipelineConfig
    : activePipelineConfigEntries !== undefined ||
      activePipelineConfigError !== undefined
  const isPipelineConfigScopeLoading =
    !hasLoadedSelectedPipelineConfig || !hasResolvedPreviewPreference
  const pipelineConfigScopeError = isPreviewMode
    ? previewPipelineConfigError
    : activePipelineConfigError
  const retryPipelineConfigScope = useCallback(async () => {
    await (isPreviewMode
      ? mutatePreviewPipelineConfig()
      : mutateActivePipelineConfig())
  }, [isPreviewMode, mutateActivePipelineConfig, mutatePreviewPipelineConfig])
  const pipelineConfigCacheScope =
    isPipelineConfigScopeLoading || !selectedPipelineConfigId
      ? undefined
      : `${siteID}:${selectedPipelineConfigId}`

  useClearRolodexEntriesCachedByIdOnScopeChange(pipelineConfigCacheScope)

  useEffect(() => {
    setHasResolvedPreviewPreference(false)
    setIsPreviewModeState(false)
  }, [siteID])

  useEffect(() => {
    if (forceActiveConfig) {
      setIsPreviewModeState(false)
      setHasResolvedPreviewPreference(true)
      return
    }
    if (!hasLoadedPreviewPipelineConfig) return

    if (!hasPreviewConfig) {
      setIsPreviewModeState(false)
      // Only clear stored preview preference on confirmed "no preview config".
      if (previewPipelineConfigError === undefined) {
        clearPreviewModePreference(siteID)
      }
      setHasResolvedPreviewPreference(true)
      return
    }

    const storedPreference = readPreviewModePreference(siteID)
    setIsPreviewModeState(storedPreference ?? true)
    setHasResolvedPreviewPreference(true)
  }, [
    forceActiveConfig,
    hasLoadedPreviewPipelineConfig,
    hasPreviewConfig,
    previewPipelineConfigError,
    siteID,
  ])

  const setIsPreviewMode = useCallback<
    React.Dispatch<React.SetStateAction<boolean>>
  >(
    (value) => {
      setIsPreviewModeState((currentValue) => {
        const nextValue =
          typeof value === 'function' ? value(currentValue) : value
        writePreviewModePreference(siteID, nextValue)
        return nextValue
      })
    },
    [siteID]
  )

  const getPipelineConfigScopedTags = useCallback(
    (tags: PipelineConfigTags): PipelineConfigTags | undefined => {
      if (!selectedPipelineConfigId) {
        return undefined
      }

      const tagsWithoutConfig = { ...tags }
      delete tagsWithoutConfig['pipeline-config']

      return {
        ...tagsWithoutConfig,
        'pipeline-config': selectedPipelineConfigId,
      }
    },
    [selectedPipelineConfigId]
  )

  const contextValue = useMemo(
    () => ({
      isPreviewMode,
      setIsPreviewMode,
      activePipelineConfigId,
      previewPipelineConfigId,
      selectedPipelineConfigId,
      pipelineConfigCacheScope,
      hasPreviewConfig,
      pipelineConfigScopeError,
      retryPipelineConfigScope,
      isPipelineConfigScopeLoading,
      getPipelineConfigScopedTags,
    }),
    [
      activePipelineConfigId,
      getPipelineConfigScopedTags,
      hasPreviewConfig,
      pipelineConfigScopeError,
      retryPipelineConfigScope,
      isPipelineConfigScopeLoading,
      isPreviewMode,
      pipelineConfigCacheScope,
      previewPipelineConfigId,
      selectedPipelineConfigId,
      setIsPreviewMode,
    ]
  )

  return (
    <PipelineConfigContext.Provider value={contextValue}>
      {children}
    </PipelineConfigContext.Provider>
  )
}

export const usePipelineConfig = (): PipelineConfigContextType => {
  const context = useContext(PipelineConfigContext)
  if (context === undefined) {
    throw new Error(
      'usePipelineConfig must be used within a PipelineConfigProvider'
    )
  }
  return context
}
